import { AlertVM, AlertPriority, AlertStatus } from '../types/alerts'
import { BackendAlert } from '../types/backend'

/**
 * Maps backend alert priority (0.0-1.0) to frontend AlertPriority enum
 */
function mapPriority(alertPriority: number): AlertPriority {
  if (alertPriority >= 0.8) return 'Urgent'
  if (alertPriority >= 0.6) return 'High'
  if (alertPriority >= 0.4) return 'Medium'
  return 'Low'
}

/**
 * Maps backend status to frontend AlertStatus
 */
function mapStatus(backendStatus: string): AlertStatus {
  // Backend uses "Acted", frontend uses "Actioned"
  if (backendStatus === 'Acted') return 'Actioned'
  return backendStatus as AlertStatus
}

/**
 * Maps backend alert type and match source type to frontend match type string
 * Uses actual backend match type values: needs_and_eligible, needs_match_ineligible, target_match, external_signal
 */
export function mapMatchType(alert: BackendAlert): string | undefined {
  // External signal alerts - simply show "External"
  if (alert.alert_type === 'external_signal' || alert.match.external_signal_metadata) {
    return 'External'
  }

  // Check for match_type field (if present in match object)
  const matchType = alert.match.match_type
  if (matchType) {
    const matchTypeMap: Record<string, string> = {
      'needs_and_eligible': 'Needs Match',
      'needs_match_ineligible': 'Needs Match',
      'target_match': 'Ideal Profile',
    }
    return matchTypeMap[matchType] || 'General Match'
  }

  // Fallback to match_source_type
  if (alert.match.match_source_type === 'needs-based') {
    return 'Needs Match'
  }

  if (alert.match.match_source_type === 'external_signal') {
    return 'General'
  }

  // Default fallback
  return 'General Match'
}

/**
 * Check if alert is an external signal (to show "External" instead of priority)
 */
export function isExternalSignal(alert: BackendAlert): boolean {
  return alert.alert_type === 'external_signal' || !!alert.match.external_signal_metadata
}

/**
 * Maps backend alert to frontend opportunity type
 */
function mapOpportunityType(alert: BackendAlert): string | undefined {
  // Check opportunity context
  const oppContext = alert.opportunity_context?.toLowerCase() || ''
  if (oppContext.includes('renewal')) return 'Renewal'
  if (oppContext.includes('upsell')) return 'Upsell'
  if (oppContext.includes('cross-sell') || oppContext.includes('crosssell')) return 'Cross-Sell'
  if (oppContext.includes('churn')) return 'Churn Risk'

  // Check account tier (Enterprise often means upsell opportunity)
  if (alert.match.account.tier === 'Enterprise') return 'Upsell'

  return undefined
}

/**
 * Maps backend alert to frontend summary string (account benefit one-liner)
 * Uses match_reason as primary source (explains why this is a match and often contains benefit language)
 * For external signals, uses evidence summary or description
 */
function mapSummary(alert: BackendAlert): string {
  // For external signals, use evidence summary or title
  if (alert.alert_type === 'external_signal' || alert.match.external_signal_metadata) {
    const evidence = alert.match.external_signal_metadata?.evidence
    if (evidence?.summary) {
      return evidence.summary
    }
    if (evidence?.title) {
      const source = alert.match.external_signal_metadata?.source_name
      return source ? `${source}: ${evidence.title}` : evidence.title
    }
    // Fallback to match_reason (explains why this external signal is relevant)
    return alert.match.match_reason
  }

  // For needs-based alerts, use match_reason as primary (explains the benefit/match)
  // Optionally enhance with context if available
  const context = alert.match.context
  const matchReason = alert.match.match_reason
  
  // If match_reason is substantial, use it as-is
  // Otherwise, enhance with context
  if (matchReason && matchReason.length > 20) {
    // Match reason is substantial, use it
    return matchReason
  }

  // Build enhanced summary from match_reason + context
  const parts: string[] = [matchReason]

  if (context.need_canonical_text && context.need_canonical_text !== matchReason) {
    parts.push(context.need_canonical_text)
  }

  if (context.need_account_impact_text) {
    parts.push(context.need_account_impact_text)
  }

  if (context.need_frequency > 1) {
    parts.push(`Mentioned ${context.need_frequency} times (latest ${formatRecency(context.need_last_mentioned)})`)
  }

  if (typeof context.need_sentiment_score === 'number') {
    parts.push(`Sentiment ${formatTrend(context.need_sentiment_score, context.need_sentiment_trend)}`)
  }

  if (typeof context.need_severity_score === 'number') {
    parts.push(`Severity ${formatTrend(context.need_severity_score, context.need_severity_trend)}`)
  }

  if (typeof context.need_impact_score === 'number') {
    parts.push(`Impact score ${(context.need_impact_score * 100).toFixed(0)}%`)
  }

  if (context.business_context && context.business_context !== matchReason) {
    parts.push(context.business_context)
  }

  return parts.join('. ')
}

/**
 * Maps a backend Alert to frontend AlertVM
 */
export function mapAlertToVM(alert: BackendAlert): AlertVM {
  const isExternal = isExternalSignal(alert)
  const externalSourceName = alert.match.external_signal_metadata?.source_name
  
  return {
    id: alert.alert_id,
    title: alert.match.account.name,
    accountName: alert.match.account.name,
    featureName: alert.match.feature.name,
    opportunityValue: alert.match.context.lost_value || undefined,
    actionRecommendation: alert.action_recommendation,
    matchType: mapMatchType(alert),
    opportunityType: mapOpportunityType(alert),
    featureStatus: alert.match.feature.status,
    priority: mapPriority(alert.alert_priority), // Show priority for both internal and external signals
    status: mapStatus(alert.status),
    createdAt: alert.created_at,
    summary: mapSummary(alert),
    matchConfidencePct: Math.round(alert.match.confidence * 100),
    badges: generateBadges(alert),
    isExternalSignal: isExternal, // Add flag for external signals
    externalSourceName: externalSourceName, // Source name for external signals
    sections: buildSections(alert),
  }
}

/**
 * Generates badges for the alert based on its type and metadata
 */
function generateBadges(alert: BackendAlert): Array<{ label: string; color?: 'red' | 'orange' | 'green' | 'blue' | 'gray' }> {
  const badges: Array<{ label: string; color?: 'red' | 'orange' | 'green' | 'blue' | 'gray' }> = []

  // Only show core health cues in the header for now
  const ctx = alert.match.context as any
  if (typeof ctx.account_health_score === 'number') {
    const score = ctx.account_health_score
    let healthLabel: string
    if (score >= 0.75) {
      healthLabel = 'Account Health: Strong'
    } else if (score >= 0.5) {
      healthLabel = 'Account Health: Moderate'
    } else {
      healthLabel = 'Account Health: At Risk'
    }
    badges.push({
      label: healthLabel,
      color: score >= 0.75 ? 'green' : score >= 0.5 ? 'orange' : 'red',
    })
  }
  if (typeof ctx.weighted_pipeline === 'number' && ctx.weighted_pipeline > 0) {
    badges.push({ label: `Weighted Pipeline ${formatCurrency(ctx.weighted_pipeline)}`, color: 'blue' })
  }

  return badges
}

/**
 * Builds UI sections from enriched alert payload (Customer Context, Feature Fit, Outcomes, Actions).
 */
function buildSections(alert: BackendAlert): AlertVM['sections'] {
  const sections: AlertVM['sections'] = []
  const ctx = alert.match.context as any
  const feature = alert.match.feature as any
  const caseStudies = (alert.match.case_studies || []).slice(0, 3)

  const contextItems: NonNullable<AlertVM['sections']>[number]['items'] = []
  const contextChips: NonNullable<AlertVM['sections']>[number]['chips'] = []
  let contextHighlight: NonNullable<AlertVM['sections']>[number]['highlight']
  const matchType = alert.match.match_type
  const isNeedsMatch =
    alert.match.match_source_type === 'needs-based' ||
    matchType === 'needs_and_eligible' ||
    matchType === 'needs_match_ineligible'

  if (isNeedsMatch) {
    if (Array.isArray(ctx.primary_categories) && ctx.primary_categories.length) {
      ctx.primary_categories.forEach((category: string) => {
        if (category) {
          contextChips.push({ label: category, tone: 'primary' })
        }
      })
    }
    if (Array.isArray(ctx.secondary_categories) && ctx.secondary_categories.length) {
      ctx.secondary_categories.forEach((category: string) => {
        if (category) {
          contextChips.push({ label: category, tone: 'secondary' })
        }
      })
    }
    const needText = ctx.need_canonical_text || ctx.source || alert.match.match_reason
    if (needText) {
      const trimmed = needText.trim().replace(/^["“”]+|["“”]+$/g, '')
      contextHighlight = { text: `“${trimmed}”` }
    }
  } else if (ctx.need_canonical_text) {
    const trimmed = ctx.need_canonical_text.trim().replace(/^["“”]+|["“”]+$/g, '')
    contextHighlight = { text: `“${trimmed}”` }
  }

  if (ctx.need_account_impact_text) {
    contextItems.push({ label: 'Impact', value: ctx.need_account_impact_text, tone: 'negative' })
  } else if (ctx.business_context) {
    contextItems.push({ label: 'Context', value: ctx.business_context })
  }

  if (ctx.need_pipeline_impact_text) {
    contextItems.push({ label: 'Pipeline Impact', value: ctx.need_pipeline_impact_text })
  }

  if (isNeedsMatch && typeof ctx.need_frequency === 'number') {
    const frequency = ctx.need_frequency
    const latest = formatRecency(ctx.need_last_mentioned)
    const lastType = ctx.need_last_engagement_type || ctx.last_engagement_type
    contextItems.push({
      label: 'Mentions',
      value: `${frequency} ${frequency === 1 ? 'time' : 'times'}, last mentioned ${latest}${lastType ? ` in ${formatEngagementType(lastType)}` : ''}`,
    })
  }

  const sentiment = describeSentiment(ctx.need_sentiment_score, ctx.need_sentiment_trend)
  if (sentiment) {
    contextItems.push({
      label: 'Sentiment',
      value: sentiment.value,
      tone: sentiment.tone,
      trendChip: sentiment.trendChip,
    })
  }
  const severity = describeSeverity(ctx.need_severity_score, ctx.need_severity_trend)
  if (severity) {
    contextItems.push({
      label: 'Severity',
      value: severity.value,
      tone: severity.tone,
      trendChip: severity.trendChip,
    })
  }

  if (typeof ctx.need_impact_score === 'number') {
    contextItems.push({
      label: 'Impact Score',
      value: `${(ctx.need_impact_score * 100).toFixed(0)}%`,
    })
  }

  if (contextItems.length || contextHighlight) {
    sections.push({
      id: 'customer-needs',
      title: 'Customer Needs & Pain Points',
      items: contextItems.length ? contextItems : undefined,
      highlight: contextHighlight
        ? {
            ...contextHighlight,
            chips: contextChips.length ? contextChips : undefined,
          }
        : undefined,
    })
  }

  if (Array.isArray(ctx.engagement_history) && ctx.engagement_history.length) {
    // Parse engagement strings into structured format
    // Expected format from backend: "YYYY-MM-DD – Type: Description [URL]" or similar
    const parsedEngagements = ctx.engagement_history.map((eng: string) => {
      // Try to parse common formats
      // Format 1: "Apr 10 – Type: Description [URL]"
      // Format 2: "2025-04-10 – Type: Description [URL]"
      // Format 3: Just a description string (fallback)
      const urlMatch = eng.match(/\[(https?:\/\/[^\]]+)\]/)
      const url = urlMatch ? urlMatch[1] : undefined
      const engWithoutUrl = url ? eng.replace(/\s*\[https?:\/\/[^\]]+\]\s*$/, '').trim() : eng
      
      const match = engWithoutUrl.match(/^(\w+\s+\d+|[\d-]+)\s*[–-]\s*([^:]+):\s*(.+)$/)
      if (match) {
        return {
          date: match[1].trim(),
          type: match[2].trim(),
          description: match[3].trim(),
          url: url,
        }
      }
      // Fallback: try to extract date and type from string
      const dateMatch = engWithoutUrl.match(/^(\w+\s+\d+|[\d-]+)/)
      const typeMatch = engWithoutUrl.match(/\b(email|task|call|gong_call|meeting|note|case|opportunity)\b/i)
      return {
        date: dateMatch ? dateMatch[1] : 'Recent',
        type: typeMatch ? formatEngagementType(typeMatch[1]) : 'Engagement',
        description: engWithoutUrl,
        url: url,
      }
    })

    sections.push({
      id: 'relevant-engagements',
      title: 'Relevant Engagements',
      engagements: parsedEngagements,
      collapsible: true,
      defaultOpen: false,
    })
  }

  const featureItems: NonNullable<AlertVM['sections']>[number]['items'] = []
  if (alert.match.eligibility_summary) {
    featureItems.push({
      label: 'Eligibility',
      value: alert.match.eligibility_summary,
      tone: alert.match.eligibility_summary.toLowerCase().includes('not eligible') ? 'negative' : 'positive',
    })
  }
  if (feature.description) {
    featureItems.push({ label: 'Overview', value: feature.description })
  }
  if (Array.isArray(feature.use_cases) && feature.use_cases.length) {
    featureItems.push({ label: 'Use Cases', value: feature.use_cases.join('; ') })
  }
  if (Array.isArray(feature.benefits) && feature.benefits.length) {
    featureItems.push({ label: 'Benefits', value: feature.benefits.join('; ') })
  }
  if (feature.requirements) {
    featureItems.push({ label: 'Requirements', value: formatRequirements(feature.requirements) })
  }
  if (Array.isArray(feature.warnings) && feature.warnings.length) {
    featureItems.push({ label: 'Warnings', value: feature.warnings.join('; ') })
  }

  if (featureItems.length || feature.docs_url) {
    sections.push({
      id: 'feature-fit',
      title: 'Feature Fit',
      items: featureItems.length ? featureItems : undefined,
      link: feature.docs_url ? { label: 'View Documentation', href: feature.docs_url } : undefined,
    })
  }

  const outcomeItems: NonNullable<AlertVM['sections']>[number]['items'] = []
  if (Array.isArray(feature.benefits) && feature.benefits.length) {
    outcomeItems.push({ label: 'Benefits', value: feature.benefits.join('; ') })
  }

    const caseStudyLines = caseStudies.map((cs: any) => formatCaseStudy(cs)).filter(Boolean)

  if (outcomeItems.length || caseStudyLines.length) {
      sections.push({
        id: 'outcomes',
        title: 'Expected Outcomes & Proof',
      items: outcomeItems.length ? outcomeItems : undefined,
      bullets: caseStudyLines.length ? caseStudyLines : undefined,
    })
  }

  const healthItems: NonNullable<AlertVM['sections']>[number]['items'] = []
  if (typeof ctx.account_health_score === 'number') {
    const score = ctx.account_health_score
    let healthValue: string
    if (score >= 0.75) {
      healthValue = 'Strong'
    } else if (score >= 0.5) {
      healthValue = 'Moderate'
    } else {
      healthValue = 'At Risk'
    }
    healthItems.push({
      label: 'Account Health',
      value: healthValue,
      tone: score >= 0.6 ? 'positive' : 'negative',
    })
  }
  if (typeof ctx.engagement_velocity_trend === 'number') {
    const trendPct = Math.round(ctx.engagement_velocity_trend * 100)
    healthItems.push({
      label: 'Engagement Velocity',
      value: `${trendPct >= 0 ? '+' : ''}${trendPct}%`,
      tone: trendPct >= 0 ? 'positive' : 'negative',
    })
  }
  if (typeof ctx.weighted_pipeline === 'number' && ctx.weighted_pipeline > 0) {
    healthItems.push({
      label: 'Weighted Pipeline',
      value: formatCurrency(ctx.weighted_pipeline),
    })
  }
  if (typeof ctx.win_rate === 'number') {
    healthItems.push({
      label: 'Win Rate',
      value: `${(ctx.win_rate * 100).toFixed(0)}%`,
      tone: ctx.win_rate >= 0.5 ? 'positive' : 'neutral',
    })
  }
  if (healthItems.length) {
    sections.push({
      id: 'health-signals',
      title: 'Health & Pipeline Signals',
      items: healthItems,
    })
  }

  const actionItems: NonNullable<AlertVM['sections']>[number]['items'] = []
  if (alert.match.account.owner) {
    actionItems.push({ label: 'Owner', value: alert.match.account.owner })
  }
  if (alert.opportunity_context) {
    actionItems.push({ label: 'Opportunity', value: alert.opportunity_context })
  }
  if (alert.next_meeting) {
    actionItems.push({ label: 'Next Meeting', value: new Date(alert.next_meeting).toLocaleString() })
  }
  if (alert.suggested_timing) {
    actionItems.push({ label: 'Timing', value: alert.suggested_timing })
  }

  const actionBullets = parseActionRecommendation(alert.action_recommendation)

  if (actionItems.length || actionBullets.length) {
    sections.push({
      id: 'actions',
      title: 'Next Steps',
      items: actionItems.length ? actionItems : undefined,
      bullets: actionBullets.length ? actionBullets : undefined,
    })
  }

  return sections.length ? sections : undefined
}

function formatRecency(value?: string | Date | null): string {
  if (!value) return 'recently'
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return 'recently'
  const diffMs = Date.now() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays <= 0) return 'today'
  if (diffDays === 1) return '1 day ago'
  if (diffDays < 30) return `${diffDays} days ago`
  const diffMonths = Math.floor(diffDays / 30)
  if (diffMonths === 1) return '1 month ago'
  return `${diffMonths} months ago`
}

function formatTrend(score: number, trend?: string | null): string {
  const formattedScore = (score * 100).toFixed(0) + '%'
  if (!trend) return formattedScore
  const trendLabel = trend.toLowerCase()
  if (trendLabel.includes('worsening') || trendLabel.includes('down')) return `${formattedScore} ↓`
  if (trendLabel.includes('improving') || trendLabel.includes('up')) return `${formattedScore} ↑`
  return `${formattedScore} →`
}

function formatRequirements(requirements: Record<string, Array<Record<string, any>>>): string {
  const lines: string[] = []
  Object.entries(requirements).forEach(([category, reqList]) => {
    reqList.forEach((req) => {
      const severity = req?.severity ? ` (${req.severity})` : ''
      lines.push(`${category}: ${req?.text || ''}${severity}`)
    })
  })
  return lines.join('; ')
}

function formatCaseStudy(cs: any): string {
  const title = cs.title || 'Unnamed Case Study'
  const summary = cs.summary || cs.results || ''
  const link = cs.source_link || cs.source_url
  return link ? `${title}: ${summary} | ${link}` : `${title}: ${summary}`
}

function describeSentiment(
  score?: number,
  trend?: string | null
): { value: string; tone: 'positive' | 'negative' | 'neutral'; trendChip?: { label: string; tone: 'positive' | 'negative' | 'neutral' } } | null {
  if (typeof score !== 'number') return null
  let descriptor: string
  let tone: 'positive' | 'negative' | 'neutral'

  if (score >= 0.7) {
    descriptor = 'Positive'
    tone = 'positive'
  } else if (score >= 0.5) {
    descriptor = 'Neutral'
    tone = 'neutral'
  } else if (score >= 0.3) {
    descriptor = 'Negative'
    tone = 'negative'
  } else {
    descriptor = 'Very Negative'
    tone = 'negative'
  }

  const trendChip = buildTrendChip('Sentiment', trend)
  return {
    value: descriptor,
    tone,
    trendChip: trendChip || undefined,
  }
}

function describeSeverity(
  score?: number,
  trend?: string | null
): { value: string; tone: 'positive' | 'negative' | 'neutral'; trendChip?: { label: string; tone: 'positive' | 'negative' | 'neutral' } } | null {
  if (typeof score !== 'number') return null
  let descriptor: string
  let tone: 'positive' | 'negative' | 'neutral'

  if (score >= 0.75) {
    descriptor = 'High'
    tone = 'negative'
  } else if (score >= 0.45) {
    descriptor = 'Moderate'
    tone = 'neutral'
  } else {
    descriptor = 'Low'
    tone = 'positive'
  }

  const trendChip = buildTrendChip('Severity', trend)
  return {
    value: descriptor,
    tone,
    trendChip: trendChip || undefined,
  }
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
}

function buildTrendChip(
  prefix: string,
  trend?: string | null
): { label: string; tone: 'positive' | 'negative' | 'neutral' } | null {
  if (!trend) return null
  const normalized = trend.toLowerCase()
  if (normalized.includes('improving') || normalized.includes('up')) {
    return { label: `${prefix} Trend ↗`, tone: 'positive' }
  }
  if (normalized.includes('worsening') || normalized.includes('down')) {
    return { label: `${prefix} Trend ↘`, tone: 'negative' }
  }
  if (normalized.includes('stable') || normalized.includes('flat')) {
    return { label: `${prefix} Trend →`, tone: 'neutral' }
  }
  return { label: `${prefix} Trend →`, tone: 'neutral' }
}

function parseActionRecommendation(recommendation?: string): string[] {
  if (!recommendation) return []
  return recommendation
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => line.replace(/^[\-\d\.\)\s]+/, '').trim())
}

function formatEngagementType(value?: string | null): string {
  if (!value) return 'Engagement'
  const normalized = value.toLowerCase().trim()
  
  // Map actual engagement types from data model
  if (normalized === 'email' || normalized.includes('email')) return 'Email'
  if (normalized === 'task' || normalized.includes('task')) return 'Task'
  if (normalized === 'gong_call' || normalized === 'call' || normalized.includes('call')) return 'Gong Call'
  if (normalized === 'meeting' || normalized.includes('meeting')) return 'Meeting'
  if (normalized === 'note' || normalized.includes('note')) return 'Note'
  if (normalized === 'case' || normalized.includes('case') || normalized.includes('ticket')) return 'Support Ticket'
  if (normalized === 'opportunity' || normalized.includes('opportunity')) return 'Opportunity'
  
  // Capitalize first letter if it's a single word
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
}

